import React, { useState, useEffect } from 'react';
import './ResetPasswordModal.css';

const ResetPasswordModal = ({ isOpen, onClose, token, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    symbol: false
  });

  // Validar contraseña en tiempo real
  useEffect(() => {
    const validation = {
      length: newPassword.length >= 8 && newPassword.length <= 20,
      uppercase: /[A-Z]/.test(newPassword),
      symbol: /[@!*]/.test(newPassword)
    };
    setPasswordValidation(validation);
  }, [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword.trim()) {
      setError('Por favor ingresa una nueva contraseña');
      return;
    }

    if (!confirmPassword.trim()) {
      setError('Por favor confirma tu nueva contraseña');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (!passwordValidation.length || !passwordValidation.uppercase || !passwordValidation.symbol) {
      setError('La contraseña no cumple con todos los requisitos');
      return;
    }

    if (!token) {
      setError('Token de recuperación no válido');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch('/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token: token,
          newPassword: newPassword.trim()
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage('¡Contraseña actualizada exitosamente!');
        setNewPassword('');
        setConfirmPassword('');
        // Cerrar modal después de 3 segundos
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
        }, 3000);
      } else {
        setError(data.message || 'Error al actualizar la contraseña');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error de conexión. Por favor intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content reset-password-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={handleClose}>
          <i className="fas fa-times"></i>
        </button>

        <div className="modal-header">
          <div className="modal-icon">
            <i className="fas fa-lock"></i>
          </div>
          <h2>Nueva Contraseña</h2>
          <p>Crea una contraseña segura para tu cuenta</p>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="newPassword">Nueva Contraseña *</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Ingresa tu nueva contraseña"
              required
              disabled={isLoading}
              className={error ? 'error' : ''}
            />
            
            {/* Validación de contraseña en tiempo real */}
            {newPassword && (
              <div className="password-validation">
                <div className={`validation-item ${passwordValidation.length ? 'valid' : 'invalid'}`}>
                  <i className={`fas ${passwordValidation.length ? 'fa-check' : 'fa-times'}`}></i>
                  <span>8-20 caracteres</span>
                </div>
                <div className={`validation-item ${passwordValidation.uppercase ? 'valid' : 'invalid'}`}>
                  <i className={`fas ${passwordValidation.uppercase ? 'fa-check' : 'fa-times'}`}></i>
                  <span>1 mayúscula</span>
                </div>
                <div className={`validation-item ${passwordValidation.symbol ? 'valid' : 'invalid'}`}>
                  <i className={`fas ${passwordValidation.symbol ? 'fa-check' : 'fa-times'}`}></i>
                  <span>1 símbolo (@, !, *)</span>
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Contraseña *</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirma tu nueva contraseña"
              required
              disabled={isLoading}
              className={error ? 'error' : ''}
            />
          </div>

          {error && <span className="error-message">{error}</span>}

          {message && (
            <div className="success-message">
              <i className="fas fa-check-circle"></i>
              <p>{message}</p>
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading || !passwordValidation.length || !passwordValidation.uppercase || !passwordValidation.symbol}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Actualizando...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Actualizar Contraseña
                </>
              )}
            </button>
          </div>
        </form>

        <div className="modal-footer">
          <p>
            ¿Recordaste tu contraseña?{' '}
            <button type="button" className="link-button" onClick={handleClose}>
              Iniciar sesión
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
